import React, {useContext} from 'react';
import * as R from 'ramda';

import {present} from '@/util';

import BaseError from '@/Form/BaseError';
import ErrorTooltip from '@/Form/ErrorTooltip';

import Text from '@/Form/Text';
import Select from '@/Form/Select';
import ReactSelect from '@/Form/ReactSelect';
import RadioGroup from '@/Form/RadioGroup';
import Radio from '@/Form/Radio';
import DatePicker from '@/Form/DatePicker';

const formDataCtx = React.createContext();
const setFormDataCtx = React.createContext();
const fieldStateCtx = React.createContext();

const nameToPath = R.compose(
  R.map(s => (Number.isNaN(Number(s)) ? s : Number(s))),
  R.split(/\W+/)
);

const targetChanges = R.compose(
  R.ifElse(
    R.propEq('type', 'checkbox'),
    R.props(['name', 'checked']),
    R.props(['name', 'value'])
  ),
  R.prop('target')
);

const handleChange = setFormData => event => {
  const [name, value] = targetChanges(event);

  setFormData(
    R.compose(
      R.assocPath(nameToPath(name), value),
      R.dissocPath(['errors', name])
    )
  );
};

const useFieldState = ({id, name}) => {
  const formData = useContext(formDataCtx);
  const setFormData = useContext(setFormDataCtx);

  const errors = R.path(['errors', name], formData);
  return {
    id: id || name,
    name,
    invalid: present(errors),
    errors,
    value: R.path(nameToPath(name), formData),
    onChange: handleChange(setFormData),
  };
};

const Form = ({stateHook, children, ...rest}) => {
  const [formData, setFormData] = stateHook;

  return (
    <setFormDataCtx.Provider value={setFormData}>
      <formDataCtx.Provider value={formData}>
        <form autoComplete='off' {...rest}>
          <BaseError errors={formData.errors} />
          {children}
        </form>
      </formDataCtx.Provider>
    </setFormDataCtx.Provider>
  );
};

export {useFieldState, fieldStateCtx};
export {Text, Select, ReactSelect, RadioGroup, Radio, ErrorTooltip, DatePicker};
export default Form;
